import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useI18n } from "@/i18n";

export function AuthPage() {
  const { t } = useI18n();
  const [mode, setMode] = useState<"login" | "signup" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { display_name: displayName } },
        });
        if (error) throw error;
      } else if (mode === "reset") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
        setResetSent(true);
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("error"));
    } finally {
      setBusy(false);
    }
  }

  function switchMode(next: "login" | "signup" | "reset") {
    setMode(next);
    setError(null);
    setResetSent(false);
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center p-6">
      <div className="card">
        <h1 className="mb-1 text-2xl font-bold text-accent">{t("appName")}</h1>
        <p className="mb-6 text-sm text-muted">
          {mode === "login"
            ? t("login")
            : mode === "signup"
              ? t("signup")
              : t("resetPassword")}
        </p>

        {mode === "reset" && resetSent ? (
          <div className="space-y-4">
            <p className="text-sm text-green-600">{t("resetEmailSent")}</p>
            <button
              className="btn-ghost w-full"
              onClick={() => switchMode("login")}
            >
              {t("backToLogin")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <label className="label">{t("displayName")}</label>
                <input
                  className="input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                />
              </div>
            )}
            <div>
              <label className="label">{t("email")}</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {mode === "reset" ? (
              <p className="text-sm text-muted">{t("resetPasswordHint")}</p>
            ) : (
              <div>
                <label className="label">{t("password")}</label>
                <input
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button className="btn-primary w-full" disabled={busy}>
              {busy
                ? t("loading")
                : mode === "login"
                  ? t("login")
                  : mode === "signup"
                    ? t("signup")
                    : t("sendResetLink")}
            </button>
          </form>
        )}

        {mode === "login" && (
          <button
            className="mt-3 w-full text-sm text-muted"
            onClick={() => switchMode("reset")}
          >
            {t("forgotPassword")}
          </button>
        )}

        {mode !== "reset" && (
          <button
            className="mt-4 w-full text-sm text-accent"
            onClick={() => switchMode(mode === "login" ? "signup" : "login")}
          >
            {mode === "login" ? t("noAccount") : t("haveAccount")}
          </button>
        )}

        {mode === "reset" && !resetSent && (
          <button
            className="mt-4 w-full text-sm text-accent"
            onClick={() => switchMode("login")}
          >
            {t("backToLogin")}
          </button>
        )}
      </div>
    </div>
  );
}
